import { useRef, useState, type TouchEvent } from 'react';

const DRAG_EXTENT_PERCENTAGE = 0.38;
const CONTENT_DRAG_FACTOR = 0.6;
const MAX_CONTENT_OFFSET_PX = 140;
const FALLBACK_THRESHOLD_PX = 200;
const MIN_REFRESH_MS = 500;

const delay = (ms: number) =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

const isScrolledToTop = (target: Element, scroller: Element): boolean => {
  let node: Element | null = target;

  while (node) {
    if (node.scrollTop > 0) {
      return false;
    }

    if (node === scroller) {
      return true;
    }

    node = node.parentElement;
  }

  return true;
};

interface Params {
  isEnabled: boolean;
  onRefresh: () => Promise<unknown>;
}

export const usePullToRefresh = ({ isEnabled, onRefresh }: Params) => {
  const startYRef = useRef<number | null>(null);
  const [threshold, setThreshold] = useState(FALLBACK_THRESHOLD_PX);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isPulling = pullDistance > 0 && !isRefreshing;
  const contentOffset = Math.min(
    pullDistance * CONTENT_DRAG_FACTOR,
    MAX_CONTENT_OFFSET_PX,
  );
  const pullProgress = isRefreshing ? 1 : Math.min(pullDistance / threshold, 1);

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const scroller = event.currentTarget;
    const target = event.target;
    const isInsideScroller =
      target instanceof Element && scroller.contains(target);
    const isAtTop = isInsideScroller && isScrolledToTop(target, scroller);

    const isSingleTouch = event.touches.length === 1;

    if (!isEnabled || isRefreshing || !isAtTop || !isSingleTouch) {
      startYRef.current = null;
      return;
    }

    setThreshold(scroller.clientHeight * DRAG_EXTENT_PERCENTAGE);
    startYRef.current = event.touches[0]?.clientY ?? null;
  };

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    const startY = startYRef.current;
    const currentY = event.touches[0]?.clientY;

    if (startY === null || currentY === undefined) {
      return;
    }

    const scroller = event.currentTarget;
    const target = event.target;
    const isStillAtTop =
      event.touches.length === 1 &&
      target instanceof Element &&
      scroller.contains(target) &&
      isScrolledToTop(target, scroller);

    if (!isStillAtTop) {
      startYRef.current = null;
      setPullDistance(0);
      return;
    }

    const pulled = currentY - startY;

    setPullDistance(Math.min(Math.max(pulled, 0), threshold));
  };

  const handleTouchEnd = async () => {
    const hasReachedThreshold = pullDistance >= threshold;

    startYRef.current = null;

    if (!hasReachedThreshold) {
      setPullDistance(0);
      return;
    }

    setIsRefreshing(true);
    setPullDistance(0);

    try {
      await Promise.all([onRefresh(), delay(MIN_REFRESH_MS)]);
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
    }
  };

  return {
    contentOffset,
    pullProgress,
    isRefreshing,
    isPulling,
    pullProps: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
  };
};
